import { el } from "../../utils/dom";
import { getState } from "../../state/store";
import { renderGroups } from "./GroupsBoard";
import type { ClassData, SeatingChart, Student, Table } from "../../types/models";
import { getTableSize, calculateSeatPositions } from "./SeatingChartEditor";
import { generateInitials, stringToColor } from "@utils/avatar";

type PrintMode = 'all-on-one' | 'one-per-page';

// Table colors for printing (optimized for print)
const TABLE_COLORS_PRINT = [
    "#e8f4f8", "#f8e8f4", "#f4f8e8", "#f8f4e8",
    "#e8e8f8", "#f8e8e8", "#e8f8e8", "#f4e8f8"
];

/**
 * Open print dialog with options for print modes
 */
export function openPrintDialog(): void {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    const mode = cls.settings.mode ?? 'groups';

    // Validate content exists
    if (mode === 'groups' && !cls.lastGroups.length) return;
    if (mode === 'seating') {
        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId) ?? cls.seatingCharts?.[0];
        if (!chart || !chart.lastAssignment) return;
    }

    // Standard modal backdrop pattern
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    const dialog = el("div", {
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 500px; width: 90%; border: 1px solid var(--border);"
    });

    const title = el("h2", { style: "margin: 0 0 20px 0;" },
        mode === 'groups' ? "Print Groups" : "Print Seating Chart"
    );

    let selectedMode: PrintMode = 'all-on-one';

    // Radio option 1: All on one page
    const radio1 = el("input", { type: "radio", name: "print-mode", value: "all-on-one", checked: true }) as HTMLInputElement;
    radio1.addEventListener("change", () => { selectedMode = 'all-on-one'; });

    const option1Label = mode === 'groups'
        ? "All groups on one page"
        : "Seating chart on one page";
    const option1Desc = mode === 'groups'
        ? "Groups will flow across pages if needed"
        : "Chart will be scaled to fit the page";

    const option1 = el("label", {
        style: "display: flex; gap: 8px; padding: 12px; border: 1px solid var(--border); border-radius: 8px; margin-bottom: 12px; cursor: pointer;"
    },
        radio1,
        el("div", {},
            el("div", { style: "font-weight: 500;" }, option1Label),
            el("div", { style: "font-size: 12px; color: var(--muted);" }, option1Desc)
        )
    );

    // Radio option 2: One per page (only for groups mode)
    let option2: HTMLElement | null = null;
    if (mode === 'groups') {
        const radio2 = el("input", { type: "radio", name: "print-mode", value: "one-per-page" }) as HTMLInputElement;
        radio2.addEventListener("change", () => { selectedMode = 'one-per-page'; });

        option2 = el("label", {
            style: "display: flex; gap: 8px; padding: 12px; border: 1px solid var(--border); border-radius: 8px; margin-bottom: 20px; cursor: pointer;"
        },
            radio2,
            el("div", {},
                el("div", { style: "font-weight: 500;" }, "One group per page"),
                el("div", { style: "font-size: 12px; color: var(--muted);" }, "Each group starts on a new page")
            )
        );
    }

    // Include student names option for seating chart
    let includeNamesCheckbox: HTMLInputElement | null = null;
    let namesOption: HTMLElement | null = null;
    if (mode === 'seating') {
        includeNamesCheckbox = el("input", { type: "checkbox", checked: true }) as HTMLInputElement;
        namesOption = el("label", {
            style: "display: flex; align-items: center; gap: 8px; padding: 12px; border: 1px solid var(--border); border-radius: 8px; margin-bottom: 20px; cursor: pointer;"
        },
            includeNamesCheckbox,
            el("span", {}, "Include student names below seats")
        );
    }

    // Buttons
    const printBtn = el("button", { class: "primary", style: "flex: 1;" }, "Print");
    printBtn.addEventListener("click", () => {
        document.body.removeChild(backdrop);
        if (mode === 'groups') {
            executePrintGroups(selectedMode, cls);
        } else {
            const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId) ?? cls.seatingCharts?.[0];
            if (chart) {
                executePrintSeating(chart, cls.students, includeNamesCheckbox?.checked ?? true);
            }
        }
    });

    const cancelBtn = el("button", { style: "flex: 1;" }, "Cancel");
    cancelBtn.addEventListener("click", () => document.body.removeChild(backdrop));

    const actions = el("div", { style: "display: flex; gap: 8px;" });
    actions.append(cancelBtn, printBtn);

    dialog.append(title, option1);
    if (option2) dialog.appendChild(option2);
    if (namesOption) dialog.appendChild(namesOption);
    dialog.appendChild(actions);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) document.body.removeChild(backdrop);
    });
}

/**
 * Execute print for groups with selected mode
 */
function executePrintGroups(mode: PrintMode, cls: ClassData): void {
    // Create temporary print container
    const printContainer = el("div", {
        class: `print-container print-mode-${mode === 'all-on-one' ? 'all' : 'pages'}`
    });

    const groupsWrapper = el("div", { class: "print-groups-grid" });
    renderGroups(groupsWrapper, cls, { showControls: false, enableDragDrop: false });

    printContainer.appendChild(groupsWrapper);
    document.body.appendChild(printContainer);

    // Trigger print
    window.print();

    // Clean up after print
    const cleanup = () => {
        if (document.body.contains(printContainer)) {
            document.body.removeChild(printContainer);
        }
        window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
}

/**
 * Execute print for seating chart
 */
function executePrintSeating(chart: SeatingChart, students: Student[], includeNames: boolean): void {
    // Create temporary print container
    const printContainer = el("div", {
        class: "print-container print-mode-seating"
    });

    // Title
    const title = el("h1", {
        style: "text-align: center; margin-bottom: 24px; font-size: 24px; color: #000;"
    }, chart.name);
    printContainer.appendChild(title);

    // Calculate bounds for the chart
    let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
    for (const table of chart.tables) {
        const size = getTableSize(table);
        minX = Math.min(minX, table.x);
        minY = Math.min(minY, table.y);
        maxX = Math.max(maxX, table.x + size.width);
        maxY = Math.max(maxY, table.y + size.height);
    }

    // Add padding
    const padding = 60;
    const chartWidth = maxX - minX + padding * 2;
    const chartHeight = maxY - minY + padding * 2;

    // Chart canvas
    const chartCanvas = el("div", {
        class: "print-seating-canvas",
        style: `
            position: relative;
            width: ${chartWidth}px;
            height: ${chartHeight}px;
            margin: 0 auto;
            background: white;
            border: 2px solid #333;
        `
    });

    // Render reference markers
    if (chart.referenceMarkers) {
        for (const marker of chart.referenceMarkers) {
            const width = marker.width ?? (marker.type === "desk" ? 100 : 40);
            const height = marker.height ?? (marker.type === "desk" ? 50 : 60);

            const markerEl = el("div", {
                style: `
                    position: absolute;
                    left: ${marker.x - minX + padding}px;
                    top: ${marker.y - minY + padding}px;
                    width: ${width}px;
                    height: ${height}px;
                    background: #f5f5f5;
                    border: 2px solid #666;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                    font-size: 16px;
                `
            });
            markerEl.appendChild(el("span", {}, marker.type === "desk" ? "🖥️" : "🚪"));
            markerEl.appendChild(el("span", { style: "font-size: 9px; color: #666;" },
                marker.type === "desk" ? "Desk" : "Door"));
            chartCanvas.appendChild(markerEl);
        }
    }

    // Render tables with seats
    for (const table of chart.tables) {
        chartCanvas.appendChild(renderTableForPrint(table, chart, students, minX, minY, padding, includeNames));
    }

    printContainer.appendChild(chartCanvas);

    // Add legend if names are included
    if (includeNames) {
        const legend = el("div", {
            style: "margin-top: 24px; padding: 16px; border-top: 1px solid #333;"
        });
        const legendTitle = el("h3", { style: "margin: 0 0 12px 0; font-size: 14px; color: #000;" }, "Student List");
        const legendList = el("div", {
            style: "display: flex; flex-wrap: wrap; gap: 8px; font-size: 12px;"
        });

        // Get all assigned students
        const assignedStudents: string[] = [];
        if (chart.lastAssignment) {
            for (const studentId of chart.lastAssignment.seatAssignments.values()) {
                if (studentId) {
                    const student = students.find(s => s.id === studentId);
                    if (student) {
                        assignedStudents.push(student.name);
                    }
                }
            }
        }
        assignedStudents.sort();

        for (const name of assignedStudents) {
            legendList.appendChild(el("span", {
                style: "padding: 2px 8px; background: #f0f0f0; border-radius: 4px;"
            }, name));
        }

        legend.append(legendTitle, legendList);
        printContainer.appendChild(legend);
    }

    document.body.appendChild(printContainer);

    // Trigger print
    window.print();

    // Clean up after print
    const cleanup = () => {
        if (document.body.contains(printContainer)) {
            document.body.removeChild(printContainer);
        }
        window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
}

/**
 * Render a single table for print
 */
function renderTableForPrint(
    table: Table,
    chart: SeatingChart,
    students: Student[],
    minX: number,
    minY: number,
    padding: number,
    includeNames: boolean
): HTMLElement {
    const size = getTableSize(table);
    const colorIndex = parseInt(table.id.slice(-2), 36) % TABLE_COLORS_PRINT.length;
    const bgColor = TABLE_COLORS_PRINT[colorIndex];

    const tableEl = el("div", {
        style: `
            position: absolute;
            left: ${table.x - minX + padding}px;
            top: ${table.y - minY + padding}px;
            width: ${size.width}px;
            height: ${size.height}px;
            background: ${bgColor};
            border: 2px solid #333;
            border-radius: ${table.shape === 'round' ? '50%' : '8px'};
            transform: rotate(${table.rotation ?? 0}deg);
            transform-origin: center center;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
        `
    });

    // Render seats
    const seatPositions = calculateSeatPositions(table.shape, table.seats, size);

    for (let i = 0; i < table.seats.length; i++) {
        const seat = table.seats[i];
        const pos = seatPositions[i];
        if (!seat || !pos) continue;

        const studentId = chart.lastAssignment?.seatAssignments.get(seat.id);
        const student = studentId ? students.find(s => s.id === studentId) : null;

        const seatWrapper = el("div", {
            style: `
                position: absolute;
                left: ${pos.x - 18}px;
                top: ${pos.y - 18}px;
                display: flex;
                flex-direction: column;
                align-items: center;
            `
        });

        const bgColorSeat = student ? stringToColor(student.id) : '#f0f0f0';

        const seatEl = el("div", {
            style: `
                width: 36px;
                height: 36px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 9px;
                font-weight: 600;
                border: 2px solid #333;
                background: ${bgColorSeat};
                color: ${student ? 'white' : '#666'};
                ${!student ? 'border-style: dashed;' : ''}
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
            `
        });

        if (student) {
            seatEl.appendChild(el("span", {}, generateInitials(student.name)));
        }

        seatWrapper.appendChild(seatEl);

        // Add name label below seat if requested
        if (includeNames && student) {
            const nameLabel = el("div", {
                style: `
                    font-size: 8px;
                    color: #000;
                    text-align: center;
                    max-width: 60px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    margin-top: 2px;
                `
            }, student.name.split(' ')[0] || student.name);
            seatWrapper.appendChild(nameLabel);
        }

        tableEl.appendChild(seatWrapper);
    }

    return tableEl;
}
